import { get, isEmpty, isObject, isString, isInteger } from 'lodash-es';
import * as Sentry from '@sentry/browser';
import { ApolloError } from '@apollo/client/errors';
import { extractCustomZuoraErrorCode, extractStripeErrorCode } from '~/javascripts/lib/zuora_utils';
import { BASE_ERROR_DICTIONARY } from '~/javascripts/lib/error_dictionary';
import {
  CONTACT_SUPPORT_LINK,
  UNEXPECTED_ERROR_MESSAGE,
  NETWORK_ERROR_MESSAGE,
} from '../constants';

// NOTE: This file is copied from the GitLab implementation (https://gitlab.com/gitlab-org/gitlab/-/blob/a6bdcc896f3d21bef71d63bed3b371939a723d14/app/assets/javascripts/lib/utils/error_utils.js)
// Cleanup issue: https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/7657

// Creates an error from an error_attribute_map
export class ActiveModelError extends Error {
  constructor(errorAttributeMap = {}, ...params) {
    // Pass remaining arguments (including vendor specific ones) to parent constructor
    super(...params);

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ActiveModelError);
    }

    this.name = 'ActiveModelError';
    // Custom debugging information
    this.errorAttributeMap = errorAttributeMap;
  }
}

export function extractErrorCode(errorCode, errorMessage) {
  return (
    extractCustomZuoraErrorCode(errorCode, errorMessage) ||
    extractStripeErrorCode(errorMessage) ||
    errorCode
  );
}

export function isMalformedError(error) {
  return isString(error) || !error?.message || Boolean(error?.code);
}

export function formatMalformedError(error) {
  Sentry.setExtra('malformed error', error);
  Sentry.captureException(new Error('malformed error'));

  return new Error(error?.message, { cause: error?.code });
}

export function extractActiveModelError(error) {
  const { networkError } = error;
  if (networkError) {
    return new Error(NETWORK_ERROR_MESSAGE, { cause: networkError });
  }

  // Will not handle multiple graphQLErrors until
  // https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/7680
  const gqlError = get(error, 'graphQLErrors[0]');

  if (isMalformedError(gqlError)) {
    return formatMalformedError(gqlError);
  }

  const { errorAttributeMap, code, message } = gqlError?.extensions || {};

  const errorMessage = message || error?.message;
  const errorCause = extractErrorCode(code, errorMessage);

  return new ActiveModelError(errorAttributeMap, String(errorMessage), { cause: errorCause });
}

export function formatGraphQLErrorMessage(errors = []) {
  return new ApolloError({ graphQLErrors: errors });
}

/**
 * @typedef {Object<ErrorAttribute,ErrorType[]>} ErrorAttributeMap - Map of attributes to error details
 * @typedef {string} ErrorAttribute - the error attribute https://api.rubyonrails.org/v7.0.4.2/classes/ActiveModel/Error.html
 * @typedef {string} ErrorType - the error type https://api.rubyonrails.org/v7.0.4.2/classes/ActiveModel/Error.html
 *
 * Returns null if `errorAttributeMap` is not an Object
 * Attempts to find a match for the errorAttributeMap in errorDictionary
 * Returns match of first errorType only, if found in errorDictionary
 *
 * @param {ErrorAttributeMap} errorAttributeMap
 * @param {Object} errorDictionary
 * @returns {(null|Object)} null or dictionary matched ERROR_MAP if found
 *
 * @example { "base": ["unauthorized", "unauthenticated"] } returns UNAUTHORIZED_ERROR_MAP
 */
function getMessageFromType(errorAttributeMap = {}, errorDictionary = {}) {
  if (!isObject(errorAttributeMap)) {
    return null;
  }

  // Will not handle multiple errorTypes per errorAttribute
  // https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/7680
  return Object.keys(errorAttributeMap).reduce((_, attribute) => {
    const errorType = errorAttributeMap[attribute.toLowerCase()]?.find(
      (type) => errorDictionary[`${attribute}:${type}`.toLowerCase()],
    );
    if (errorType) {
      return errorDictionary[`${attribute}:${errorType}`.toLowerCase()];
    }

    return null;
  }, null);
}

/**
 * @param {String} cause
 * @param {Object} errorDictionary
 * @returns {(null|string)} null or error message if found
 */
function getMessageFromCause(cause, errorDictionary = {}) {
  if (!isString(cause) && !isInteger(cause)) {
    return null;
  }
  const errorType = errorDictionary[String(cause).toLocaleLowerCase()];
  if (!errorType) {
    return null;
  }

  return errorType;
}

/**
 * @example "Email has already been taken, Email is invalid"
 * // returns `${UNLINKED_ACCOUNT_ERROR}`, i.e. the `EMAIL_TAKEN_ERROR_TYPE` error message
 *
 * @param {string} errorString
 * @param {Object} errorDictionary
 * @returns {(null|string)} null or error message if found
 */
function getMessageFromErrorString(errorString, errorDictionary = {}) {
  if (isEmpty(errorString) || !isString(errorString)) {
    return null;
  }

  const messages = errorString.split(', ');
  const errorMessage = messages.find((message) => errorDictionary[message.toLowerCase()]);
  if (errorMessage) {
    return errorDictionary[errorMessage.toLowerCase()];
  }

  return {
    message: errorString,
    links: {},
  };
}

export const DEFAULT_ERROR_MAP = {
  message: UNEXPECTED_ERROR_MESSAGE,
  links: { supportLink: CONTACT_SUPPORT_LINK },
};

/**
 * Receives an Error and attempts to extract the `errorAttributeMap` in
 * case it is an `ActiveModelError` and returns the message if it exists.
 * If a match is not found it will attempt to map a message from the
 * Error.message to be returned.
 * Otherwise, it will return a general error message.
 *
 * @param {Error|Object|String} systemError
 * @param {Object} errorDictionary
 * @param {Object} defaultError
 * @returns {(Object|string)} error message
 */
// TODO: https://gitlab.com/gitlab-org/customers-gitlab-com/-/issues/10791
// Fix this the next time the file is edited.
// eslint-disable-next-line sonarjs/cognitive-complexity
export function mapSystemToFriendlyError(
  systemError,
  errorDictionary = BASE_ERROR_DICTIONARY,
  defaultError = DEFAULT_ERROR_MAP,
) {
  if (isString(systemError)) {
    const messageFromErrorString = getMessageFromErrorString(systemError, errorDictionary);
    if (messageFromErrorString) {
      return messageFromErrorString;
    }
    return defaultError;
  }

  if (!isObject(systemError)) {
    return defaultError;
  }

  const { errorAttributeMap, cause, message } = systemError;

  const messageFromType = getMessageFromType(errorAttributeMap, errorDictionary);
  if (messageFromType) {
    return messageFromType;
  }

  const messageFromCause = getMessageFromCause(cause, errorDictionary);
  if (messageFromCause) {
    return messageFromCause;
  }

  const messageFromErrorString = getMessageFromErrorString(message, errorDictionary);
  if (messageFromErrorString) {
    return messageFromErrorString;
  }

  return defaultError;
}

export function hasErrorAttributes(error) {
  return Object.keys(error?.errorAttributeMap || {}).length > 0;
}
